import { InlineIcon } from "@iconify/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { toast } from "react-toastify";
import { Loader } from "../../../components/widgets/Loader";
import { brand } from "../../../context";
import { useApoAuth } from "../../../hooks";
import { type EntityColors, updateSubAgent } from "./api";
import type { SubagentData } from "./types";

type ColorPickerModalProps = {
  onClose: () => void;
  subagentData: SubagentData;
  appUserDataQueryKey: (string | undefined)[];
  onSave?: (colors: { primaryColor: string; secondaryColor: string }) => void;
};

export function ColorPickerModal({
  onClose,
  subagentData,
  appUserDataQueryKey,
  onSave,
}: ColorPickerModalProps) {
  const [primaryColor, setPrimaryColor] = useState(subagentData.entity_colors?.base || "#000000");
  const [secondaryColor, setSecondaryColor] = useState(
    subagentData.entity_colors?.background.secondary?.dark || "#000000",
  );
  const { appUserData, getIdTokenClaims } = useApoAuth();
  const queryClient = useQueryClient();

  // Reset colors when subagentData changes
  useEffect(() => {
    setPrimaryColor(subagentData.entity_colors?.base || "#000000");
    setSecondaryColor(subagentData.entity_colors?.background.secondary?.dark || "#000000");
  }, [subagentData.entity_colors]);

  // Mutation to update entity colors
  const updateColorsMutation = useMutation({
    mutationFn: async (colors: { primaryColor: string; secondaryColor: string }) => {
      // Get authentication token
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Unable to get authentication token");
      }
      const token = (tokenClaims as { __raw: string }).__raw;

      // Get subagent_id from appUserData
      if (!appUserData?.entity_id || appUserData.entity_type !== "subagent") {
        throw new Error("User is not associated with a subagent");
      }
      const subagentId = appUserData.entity_id;

      // Construct EntityColors object
      const entityColors: EntityColors = {
        base: colors.primaryColor,
        background: {
          dark: colors.primaryColor,
          light: subagentData.entity_colors?.background.light || "",
          secondary: {
            dark: colors.secondaryColor,
            light: subagentData.entity_colors?.background.secondary?.light || "",
          },
        },
        text: subagentData.entity_colors?.text || { colored: "" },
      };

      // Update subagent with the new entity colors
      await updateSubAgent(
        {
          subagent_id: subagentId,
          entity_colors: entityColors,
        },
        token,
      );

      return colors;
    },
    onSuccess: (colors) => {
      toast.success("Colors updated successfully");
      onSave?.(colors);
      // Invalidate the app user query to refetch updated data
      queryClient.invalidateQueries({ queryKey: appUserDataQueryKey });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update colors");
    },
  });

  const handleSave = () => {
    updateColorsMutation.mutate({ primaryColor, secondaryColor });
  };

  return (
    <div
      className="Modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
    >
      <div
        className="Modal__card ColorPickerModal__card max-w-[600px]"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="Modal__card__header">
          <div>Pick Colors</div>
          <InlineIcon icon="mdi:close" className="Modal__card__header__icon" onClick={onClose} />
        </div>
        <div className="Modal__card__body" style={{ position: "relative" }}>
          {updateColorsMutation.isLoading && <Loader size="lg" />}
          <div className="grid gap-6">
            {/* Primary Color Picker */}
            <div className="grid gap-3">
              <div className="grid grid-cols-[auto_1fr] gap-3 items-center">
                <div
                  className="w-12 h-12 rounded-sm border border-[#ddd]"
                  style={{ backgroundColor: primaryColor }}
                />
                <div>
                  <div className="text-base font-medium">Primary Color</div>
                  <div className="text-sm text-gray-600 font-mono">{primaryColor}</div>
                </div>
              </div>
              <div className="grid justify-items-center py-2">
                <HexColorPicker
                  color={primaryColor}
                  onChange={setPrimaryColor}
                  style={{ width: "100%", maxWidth: "300px", height: "200px" }}
                />
              </div>
            </div>

            {/* Secondary Color Picker */}
            <div className="grid gap-3">
              <div className="grid grid-cols-[auto_1fr] gap-3 items-center">
                <div
                  className="w-12 h-12 rounded-sm border border-[#ddd]"
                  style={{ backgroundColor: secondaryColor }}
                />
                <div>
                  <div className="text-base font-medium">Secondary Color</div>
                  <div className="text-sm text-gray-600 font-mono">{secondaryColor}</div>
                </div>
              </div>
              <div className="grid justify-items-center py-2">
                <HexColorPicker
                  color={secondaryColor}
                  onChange={setSecondaryColor}
                  style={{ width: "100%", maxWidth: "300px", height: "200px" }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="Modal__card__actions grid grid-cols-2">
          <button
            type="button"
            className="Button"
            data-brand={brand}
            onClick={onClose}
            disabled={updateColorsMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="Button"
            data-brand={brand}
            onClick={handleSave}
            disabled={updateColorsMutation.isLoading}
          >
            {updateColorsMutation.isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
